import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { Loading } from 'notiflix'
import { Report } from 'notiflix/build/notiflix-report-aio'
import { Confirm } from 'notiflix/build/notiflix-confirm-aio'
import api from '../../../helpers/apiRequester'
import styles from './styles.module.scss'

interface Introducer {
    key: string
    label: string
}

type RunStatus = 'running' | 'completed' | 'failed'
type TriggeredBy = 'eventbridge' | 'manual_dashboard'

interface ScrapeRun {
    id: number
    introducer: string
    status: RunStatus
    triggered_by: TriggeredBy
    triggered_by_user: string | null
    started_at: string
    finished_at: string | null
    total_operation_time: string | null
    result_json: any | string | null
    error_message: string | null
    created_at: string
}

// Whitelisted phases. Keep in sync with the `phase` ENUM in the migration
// 1779475300-FEATURE-cron-scrape-run-errors.sql AND `SCRAPE_ERROR_PHASES`
// in utilities/helpers/cronScrapeRunRecording.js.
type ErrorPhase = 'ftp' | 'six_year' | 'fifteen_year' | 'create_claim' | 'cf_transfer' | 'other'

interface ScrapeRunError {
    id: number
    run_id: number
    introducer: string
    phase: ErrorPhase
    identifier: string | null
    error_msg: string | null
    custom_msg: string | null
    stack_trace: string | null
    error_info: any | string | null
    created_at: string
}

interface RunErrorsBundle {
    loading: boolean
    loaded: boolean
    errors: ScrapeRunError[]
    phaseCounts: Partial<Record<ErrorPhase, number>>
    failureReason?: string
}

const PHASE_DISPLAY_ORDER: ErrorPhase[] = [
    'ftp',
    'six_year',
    'fifteen_year',
    'create_claim',
    'cf_transfer',
    'other',
]

const PHASE_LABELS: Record<ErrorPhase, string> = {
    ftp: 'FTP',
    six_year: '6yr',
    fifteen_year: '15yr',
    create_claim: 'Claim Create',
    cf_transfer: 'CF Transfer',
    other: 'Other',
}

const HISTORY_LIMIT = 50

// Persisted (per-browser) list of scraper keys the user has hidden from the
// grid. Lets the team tuck away inactive introducers without any backend/config
// change. localStorage (not session) so the preference survives a browser
// restart; it's a purely cosmetic, per-user setting.
const HIDDEN_SCRAPERS_STORAGE_KEY = 'cronScrapers.hiddenIntroducers'

const loadHiddenScraperKeys = (): Set<string> => {
    try {
        const raw = localStorage.getItem(HIDDEN_SCRAPERS_STORAGE_KEY)
        if (raw) {
            const parsed = JSON.parse(raw)
            if (Array.isArray(parsed)) return new Set(parsed.filter((k) => typeof k === 'string'))
        }
    } catch {
        /* corrupt/unavailable storage — fall through to an empty set */
    }
    return new Set()
}

const formatDateTime = (raw: string | null | undefined) => {
    if (!raw) return '—'
    try {
        return new Date(raw).toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        })
    } catch {
        return raw
    }
}

const formatRelativeOrAbsolute = (raw: string | null | undefined) => {
    if (!raw) return 'Never'
    return formatDateTime(raw)
}

// MySQL JSON columns can come back as either an object (when the driver
// inflates them) or a string (when it doesn't). Normalise both into an
// object for rendering.
const parseResultJson = (raw: any | string | null): Record<string, any> | null => {
    if (raw === null || raw === undefined) return null
    if (typeof raw === 'object') return raw
    try {
        return JSON.parse(raw as string)
    } catch {
        return null
    }
}

const StatusBadge = ({ status }: { status: RunStatus | 'never' }) => {
    const className =
        status === 'running'
            ? styles.statusRunning
            : status === 'completed'
            ? styles.statusCompleted
            : status === 'failed'
            ? styles.statusFailed
            : styles.statusNever
    const label = status === 'never' ? 'Never run' : status
    return <span className={`${styles.statusBadge} ${className}`}>{label}</span>
}

// Pull the `transferSkipped` flag out of a run's result_json without forcing
// every caller to parse it themselves. Returns false for runs that completed
// before this feature shipped (the field simply won't exist on their payload).
const wasTransferSkipped = (run: ScrapeRun): boolean => {
    const result = parseResultJson(run.result_json)
    return !!result?.transferSkipped
}

const TriggerSourceBadge = ({
    triggeredBy,
    transferSkipped = false,
}: {
    triggeredBy: TriggeredBy
    transferSkipped?: boolean
}) => {
    const isManual = triggeredBy === 'manual_dashboard'
    const label = isManual
        ? transferSkipped
            ? 'Manual (no transfer)'
            : 'Manual'
        : 'Scheduled'
    const title = isManual
        ? transferSkipped
            ? 'Triggered by a dashboard user — Contact Fusion transfer phase was skipped'
            : 'Triggered by a dashboard user'
        : 'Scheduled EventBridge run'
    return (
        <span
            className={`${styles.triggerSource} ${isManual ? styles.triggerSourceManual : ''} ${
                transferSkipped ? styles.triggerSourceNoTransfer : ''
            }`}
            title={title}
        >
            {label}
        </span>
    )
}

// Subset of metrics worth surfacing on the introducer card. The result
// payload from `sendCronEmailUpdates` has dozens of fields — these are the
// ones the team actually scans for at-a-glance health.
const CARD_METRIC_KEYS: { key: string; label: string }[] = [
    { key: 'ftpFilesFound', label: 'FTP Files' },
    { key: 'totalSixYearClaimsUsable', label: '6yr Claims' },
    { key: 'totalFifteenYearClaimsUsable', label: '15yr Claims' },
    { key: 'totalAdditionalClaimDocsCreated', label: 'Docs' },
]

const renderCardMetrics = (resultJson: Record<string, any> | null) => {
    if (!resultJson) {
        return <div className={styles.emptyMetrics}>No result data yet</div>
    }
    return (
        <div className={styles.metricsRow}>
            {CARD_METRIC_KEYS.map(({ key, label }) => {
                const raw = resultJson[key]
                const display = raw === undefined || raw === null || raw === '' ? '—' : String(raw)
                return (
                    <div key={key} className={styles.metric}>
                        <span className={styles.metricValue}>{display}</span>
                        <span className={styles.metricLabel}>{label}</span>
                    </div>
                )
            })}
        </div>
    )
}

// One expanded-row's error data: fetched lazily the first time the user
// opens a particular run so we don't hammer the API loading errors for every
// row in the history table up front.
const fetchRunErrorsFromApi = async (runId: number): Promise<RunErrorsBundle> => {
    try {
        const resp = await api({
            url: 'credit-scrape-run-errors',
            data: { runId, includePhaseCounts: true, limit: 1000 },
        })
        if (!resp.data?.success) {
            return {
                loading: false,
                loaded: true,
                errors: [],
                phaseCounts: {},
                failureReason: resp.data?.message || 'Failed to load errors',
            }
        }
        return {
            loading: false,
            loaded: true,
            errors: resp.data.errors || [],
            phaseCounts: resp.data.phaseCounts || {},
        }
    } catch (err: any) {
        return {
            loading: false,
            loaded: true,
            errors: [],
            phaseCounts: {},
            failureReason: err?.response?.data?.message || err?.message || 'Failed to load errors',
        }
    }
}

const PhaseCountsStrip = ({
    phaseCounts,
    loading,
}: {
    phaseCounts: Partial<Record<ErrorPhase, number>>
    loading: boolean
}) => {
    if (loading) {
        return <div className={styles.phaseCountsStrip}>Loading error counts…</div>
    }
    const total = PHASE_DISPLAY_ORDER.reduce(
        (acc, phase) => acc + (phaseCounts[phase] || 0),
        0
    )
    return (
        <div className={styles.phaseCountsStrip}>
            <span className={styles.phaseCountsLabel}>Errors by phase:</span>
            {PHASE_DISPLAY_ORDER.map((phase) => {
                const count = phaseCounts[phase] || 0
                const hasErrors = count > 0
                return (
                    <span
                        key={phase}
                        className={`${styles.phaseCountPill} ${
                            hasErrors ? styles.phaseCountPillActive : ''
                        }`}
                    >
                        {PHASE_LABELS[phase]}: <strong>{count}</strong>
                    </span>
                )
            })}
            <span className={styles.phaseCountsTotal}>Total: {total}</span>
        </div>
    )
}

const ScrapeRunErrorsPanel = ({
    bundle,
}: {
    bundle: RunErrorsBundle | undefined
}) => {
    const [expandedErrorIds, setExpandedErrorIds] = useState<Set<number>>(new Set())
    const [collapsedPhases, setCollapsedPhases] = useState<Set<ErrorPhase>>(new Set())

    const toggleError = (id: number) => {
        setExpandedErrorIds((prev) => {
            const next = new Set(prev)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })
    }

    const togglePhase = (phase: ErrorPhase) => {
        setCollapsedPhases((prev) => {
            const next = new Set(prev)
            if (next.has(phase)) next.delete(phase)
            else next.add(phase)
            return next
        })
    }

    if (!bundle || bundle.loading) {
        return <div className={styles.errorsPanelMuted}>Loading errors…</div>
    }
    if (bundle.failureReason) {
        return (
            <div className={styles.errorsPanelMuted}>
                Could not load errors: {bundle.failureReason}
            </div>
        )
    }
    if (!bundle.errors.length) {
        return (
            <div className={styles.errorsPanelMuted}>
                No errors recorded for this run.
            </div>
        )
    }

    // Group by phase, preserving display order.
    const grouped: Partial<Record<ErrorPhase, ScrapeRunError[]>> = {}
    for (const err of bundle.errors) {
        const phase = (PHASE_DISPLAY_ORDER as ErrorPhase[]).includes(err.phase)
            ? err.phase
            : 'other'
        if (!grouped[phase]) grouped[phase] = []
        grouped[phase]!.push(err)
    }

    return (
        <div className={styles.errorsPanel}>
            <div className={styles.errorsPanelHeader}>
                Errors recorded during this run ({bundle.errors.length})
            </div>
            {PHASE_DISPLAY_ORDER.map((phase) => {
                const list = grouped[phase] || []
                if (!list.length) return null
                const collapsed = collapsedPhases.has(phase)
                return (
                    <div key={phase} className={styles.errorPhaseGroup}>
                        <button
                            type="button"
                            className={styles.errorPhaseHeader}
                            onClick={() => togglePhase(phase)}
                        >
                            <span className={styles.errorPhaseToggleIcon}>
                                {collapsed ? '▶' : '▼'}
                            </span>
                            <span className={styles.errorPhaseTitle}>
                                {PHASE_LABELS[phase]} ({list.length})
                            </span>
                        </button>
                        {!collapsed && (
                            <div className={styles.errorPhaseBody}>
                                {list.map((err) => {
                                    const isOpen = expandedErrorIds.has(err.id)
                                    return (
                                        <div key={err.id} className={styles.errorRow}>
                                            <button
                                                type="button"
                                                className={styles.errorRowHeader}
                                                onClick={() => toggleError(err.id)}
                                            >
                                                <span className={styles.errorRowToggle}>
                                                    {isOpen ? '−' : '+'}
                                                </span>
                                                <span className={styles.errorRowMsg}>
                                                    {err.custom_msg || err.error_msg || 'Unknown error'}
                                                </span>
                                                {err.identifier && (
                                                    <span className={styles.errorRowIdent}>
                                                        {err.identifier}
                                                    </span>
                                                )}
                                                <span className={styles.errorRowTime}>
                                                    {formatDateTime(err.created_at)}
                                                </span>
                                            </button>
                                            {isOpen && (
                                                <div className={styles.errorRowBody}>
                                                    {err.error_msg && (
                                                        <div className={styles.errorField}>
                                                            <span className={styles.errorFieldLabel}>
                                                                Message
                                                            </span>
                                                            <div className={styles.errorFieldValue}>
                                                                {err.error_msg}
                                                            </div>
                                                        </div>
                                                    )}
                                                    {err.stack_trace && (
                                                        <div className={styles.errorField}>
                                                            <span className={styles.errorFieldLabel}>
                                                                Stack trace
                                                            </span>
                                                            <pre
                                                                className={styles.errorStackBox}
                                                            >
                                                                {err.stack_trace}
                                                            </pre>
                                                        </div>
                                                    )}
                                                    {err.error_info && (
                                                        <div className={styles.errorField}>
                                                            <span className={styles.errorFieldLabel}>
                                                                Context
                                                            </span>
                                                            <pre
                                                                className={styles.errorStackBox}
                                                            >
                                                                {typeof err.error_info === 'string'
                                                                    ? err.error_info
                                                                    : JSON.stringify(
                                                                          err.error_info,
                                                                          null,
                                                                          2
                                                                      )}
                                                            </pre>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )
                                })}
                            </div>
                        )}
                    </div>
                )
            })}
        </div>
    )
}

export const CronScrapersView = () => {
    const [introducers, setIntroducers] = useState<Introducer[]>([])
    const [latestPerIntroducer, setLatestPerIntroducer] = useState<ScrapeRun[]>([])
    const [recentRuns, setRecentRuns] = useState<ScrapeRun[]>([])
    const [loading, setLoading] = useState(true)
    const [refreshing, setRefreshing] = useState(false)
    const [triggeringKey, setTriggeringKey] = useState<string | null>(null)
    const [resettingKey, setResettingKey] = useState<string | null>(null)
    const [filterIntroducer, setFilterIntroducer] = useState<string>('')
    const [expandedRunId, setExpandedRunId] = useState<number | null>(null)
    // Lazy-loaded cache, keyed by run id. We don't pre-fetch all errors for
    // the whole history table — only for rows the user actually opens.
    const [errorsByRunId, setErrorsByRunId] = useState<Record<number, RunErrorsBundle>>({})
    // Which card's "Run now ▾" dropdown is currently open (only one at a time).
    // Tracking by introducer key rather than a boolean lets us close-others on
    // open without juggling refs.
    const [openMenuKey, setOpenMenuKey] = useState<string | null>(null)
    // Per-browser set of scraper keys hidden from the grid (inactive scrapers).
    const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(loadHiddenScraperKeys)
    // When true, hidden scrapers are revealed (dimmed) so they can be un-hidden.
    const [showHidden, setShowHidden] = useState(false)

    // Persist the hidden set whenever it changes.
    useEffect(() => {
        try {
            localStorage.setItem(HIDDEN_SCRAPERS_STORAGE_KEY, JSON.stringify(Array.from(hiddenKeys)))
        } catch {
            /* storage unavailable (private mode / quota) — non-fatal */
        }
    }, [hiddenKeys])

    const toggleHidden = useCallback((key: string) => {
        setHiddenKeys((prev) => {
            const next = new Set(Array.from(prev))
            if (next.has(key)) next.delete(key)
            else next.add(key)
            return next
        })
    }, [])

    // Close the dropdown whenever the user clicks anywhere outside it. Uses
    // capture-phase `mousedown` so it fires before any button-internal handler.
    useEffect(() => {
        if (!openMenuKey) return
        const onDocMouseDown = (e: MouseEvent) => {
            const target = e.target as HTMLElement | null
            if (target && target.closest && target.closest(`[data-run-menu="${openMenuKey}"]`)) {
                return
            }
            setOpenMenuKey(null)
        }
        document.addEventListener('mousedown', onDocMouseDown)
        return () => document.removeEventListener('mousedown', onDocMouseDown)
    }, [openMenuKey])

    const latestByIntroducer = useMemo(() => {
        const map: Record<string, ScrapeRun | undefined> = {}
        latestPerIntroducer.forEach((row) => {
            map[row.introducer] = row
        })
        return map
    }, [latestPerIntroducer])

    // Cards to render in the grid. When the user is managing visibility we keep
    // hidden cards visible (dimmed); otherwise they're filtered out entirely.
    const visibleIntroducers = useMemo(() => {
        if (showHidden) return introducers
        return introducers.filter((introducer) => !hiddenKeys.has(introducer.key))
    }, [introducers, hiddenKeys, showHidden])

    const hiddenCount = useMemo(
        () => introducers.filter((introducer) => hiddenKeys.has(introducer.key)).length,
        [introducers, hiddenKeys]
    )

    const loadData = useCallback(async (opts: { showSpinner?: boolean } = {}) => {
        const { showSpinner = false } = opts
        if (showSpinner) Loading.circle('Loading scraper history...')
        try {
            const [introducersResp, runsResp] = await Promise.all([
                api({ url: 'credit-scrape-introducers', data: {} }),
                api({
                    url: 'credit-scrape-runs',
                    data: { limit: HISTORY_LIMIT, includeLatestPerIntroducer: true },
                }),
            ])
            if (introducersResp.data?.success) {
                setIntroducers(introducersResp.data.introducers || [])
            }
            if (runsResp.data?.success) {
                setRecentRuns(runsResp.data.runs || [])
                setLatestPerIntroducer(runsResp.data.latestPerIntroducer || [])
            }
        } catch (err: any) {
            console.error('Failed to load credit scrape data:', err)
            Report.failure(
                'Load failed',
                err?.response?.data?.message || 'Could not load scraper data. Please try again.',
                'Okay'
            )
        } finally {
            if (showSpinner) Loading.remove()
        }
    }, [])

    useEffect(() => {
        ;(async () => {
            await loadData({ showSpinner: true })
            setLoading(false)
        })()
    }, [loadData])

    const handleRefresh = useCallback(async () => {
        setRefreshing(true)
        await loadData()
        setRefreshing(false)
    }, [loadData])

    const handleTrigger = useCallback(
        (introducer: Introducer, { skipTransfer = false }: { skipTransfer?: boolean } = {}) => {
            // Manual scrapes can take 30+ minutes once spawned — we want a
            // deliberate confirm rather than a single-click foot-gun.
            const transferNote = skipTransfer
                ? ` Phase 5 (Contact Fusion transfer) will be SKIPPED — Phases 1-4 ` +
                  `(downloads, PDF/CSV scraping, document generation) still run as normal.`
                : ` All 5 phases will run including the Contact Fusion transfer.`
            Confirm.show(
                skipTransfer
                    ? `Trigger ${introducer.label} (no transfer)?`
                    : `Trigger ${introducer.label}?`,
                `This kicks off the credit-report scraper in the background.` +
                    transferNote +
                    ` Results will appear here once it finishes (anywhere from a minute to ` +
                    `30+ minutes depending on backlog). The usual completion email will ` +
                    `still be sent to the team.`,
                'Start now',
                'Cancel',
                async () => {
                    try {
                        setTriggeringKey(introducer.key)
                        Loading.circle(`Starting ${introducer.label}...`)
                        const resp = await api({
                            url: 'credit-scrape-trigger',
                            data: { introducer: introducer.key, skipTransfer },
                        })
                        if (resp.data?.success) {
                            Report.success(
                                'Scrape started',
                                `${introducer.label} is now running in the background` +
                                    (skipTransfer ? ' (no transfer)' : '') +
                                    `. Refresh the page in a few minutes to see results.`,
                                'Okay'
                            )
                            await loadData()
                        }
                    } catch (err: any) {
                        // 409 = already running — surface the existing run rather
                        // than a generic failure so the user knows to wait.
                        if (err?.response?.status === 409) {
                            const existing = err.response.data?.existing
                            Report.info(
                                'Already running',
                                `A scrape for ${introducer.label} is already in progress` +
                                    (existing?.started_at
                                        ? ` (started ${formatDateTime(existing.started_at)}).`
                                        : '.') +
                                    ` Refresh the page once it finishes.`,
                                'Okay'
                            )
                            await loadData()
                        } else {
                            Report.failure(
                                'Trigger failed',
                                err?.response?.data?.message ||
                                    'Could not start the scrape. Please try again.',
                                'Okay'
                            )
                        }
                    } finally {
                        Loading.remove()
                        setTriggeringKey(null)
                    }
                },
                () => {
                    /* user cancelled — no-op */
                }
            )
        },
        [loadData]
    )

    const handleReset = useCallback(
        (introducer: Introducer) => {
            // Scrapes run fire-and-forget inside the server process — there's no
            // handle to truly kill one. This action only clears the stuck
            // 'running' row (typically left behind by a server restart
            // mid-scrape) so the tile resets and a new run can be started.
            Confirm.show(
                `Reset ${introducer.label}?`,
                `This marks the current "running" record as interrupted so the ` +
                    `tile clears and you can start a fresh scrape. Use this when a ` +
                    `scrape was left stuck "running" — for example after the server ` +
                    `was restarted before it finished.\n\n` +
                    `Note: if a scrape really is still running in the background, this ` +
                    `won't stop it — it only resets the dashboard so a new run isn't blocked.`,
                'Reset',
                'Cancel',
                async () => {
                    try {
                        setResettingKey(introducer.key)
                        Loading.circle(`Resetting ${introducer.label}...`)
                        const resp = await api({
                            url: 'credit-scrape-reset',
                            data: { introducer: introducer.key },
                        })
                        if (resp.data?.success) {
                            const count = resp.data.resetCount ?? 0
                            if (count > 0) {
                                Report.success(
                                    'Scraper reset',
                                    `${introducer.label} has been reset. You can now start a new scrape.`,
                                    'Okay'
                                )
                            } else {
                                Report.info(
                                    'Nothing to reset',
                                    `${introducer.label} had no run stuck in "running". The tile is already clear.`,
                                    'Okay'
                                )
                            }
                            await loadData()
                        }
                    } catch (err: any) {
                        Report.failure(
                            'Reset failed',
                            err?.response?.data?.message ||
                                'Could not reset the scraper. Please try again.',
                            'Okay'
                        )
                    } finally {
                        Loading.remove()
                        setResettingKey(null)
                    }
                },
                () => {
                    /* user cancelled — no-op */
                }
            )
        },
        [loadData]
    )

    const filteredRuns = useMemo(() => {
        if (!filterIntroducer) return recentRuns
        return recentRuns.filter((row) => row.introducer === filterIntroducer)
    }, [recentRuns, filterIntroducer])

    // Lazy-load errors when a row is expanded for the first time. Re-fetches
    // are still possible via the explicit "Reload errors" button when the
    // user wants to pick up newly-recorded errors for an in-progress run.
    const loadRunErrors = useCallback(async (runId: number, force = false) => {
        if (!force) {
            const cached = errorsByRunId[runId]
            if (cached && (cached.loaded || cached.loading)) return
        }
        setErrorsByRunId((prev) => ({
            ...prev,
            [runId]: {
                loading: true,
                loaded: false,
                errors: prev[runId]?.errors || [],
                phaseCounts: prev[runId]?.phaseCounts || {},
            },
        }))
        const bundle = await fetchRunErrorsFromApi(runId)
        setErrorsByRunId((prev) => ({ ...prev, [runId]: bundle }))
    }, [errorsByRunId])

    const handleToggleExpand = useCallback(
        (runId: number) => {
            const willExpand = expandedRunId !== runId
            setExpandedRunId(willExpand ? runId : null)
            if (willExpand) {
                loadRunErrors(runId)
            }
        },
        [expandedRunId, loadRunErrors]
    )

    if (loading) {
        return <div className={styles.loadingText}>Loading...</div>
    }

    return (
        <div className={styles.container}>
            <div className={styles.actionsBar}>
                {(hiddenCount > 0 || showHidden) && (
                    <button
                        className={styles.refreshButton}
                        onClick={() => setShowHidden((v) => !v)}
                        type="button"
                    >
                        {showHidden ? 'Done managing' : `Show hidden (${hiddenCount})`}
                    </button>
                )}
                <button
                    className={styles.refreshButton}
                    onClick={handleRefresh}
                    disabled={refreshing}
                    type="button"
                >
                    {refreshing ? 'Refreshing...' : 'Refresh'}
                </button>
            </div>

            <h2 className={styles.sectionHeader}>Available Scrapers</h2>
            <div className={styles.cardsGrid}>
                {visibleIntroducers.map((introducer) => {
                    const last = latestByIntroducer[introducer.key]
                    const lastResult = parseResultJson(last?.result_json ?? null)
                    const isRunning = last?.status === 'running'
                    const isTriggering = triggeringKey === introducer.key
                    const isResetting = resettingKey === introducer.key
                    const isHidden = hiddenKeys.has(introducer.key)
                    return (
                        <div
                            key={introducer.key}
                            className={`${styles.card} ${isHidden ? styles.cardHidden : ''}`}
                        >
                            <div className={styles.cardHeader}>
                                <div>
                                    <h3 className={styles.cardTitle}>{introducer.label}</h3>
                                    <span className={styles.cardKey}>{introducer.key}</span>
                                </div>
                                <div className={styles.cardHeaderRight}>
                                    <StatusBadge status={(last?.status as RunStatus) ?? 'never'} />
                                    <button
                                        type="button"
                                        className={styles.hideToggle}
                                        onClick={() => toggleHidden(introducer.key)}
                                        title={
                                            isHidden
                                                ? 'Show this scraper on the grid'
                                                : 'Hide this scraper from the grid'
                                        }
                                    >
                                        {isHidden ? 'Show' : 'Hide'}
                                    </button>
                                </div>
                            </div>

                            <div className={styles.lastRunInfo}>
                                <span>Last run: {formatRelativeOrAbsolute(last?.started_at)}</span>
                                {last?.total_operation_time && (
                                    <span>Duration: {last.total_operation_time}</span>
                                )}
                                {last?.triggered_by === 'manual_dashboard' && last?.triggered_by_user && (
                                    <span>Triggered by: {last.triggered_by_user}</span>
                                )}
                            </div>

                            {renderCardMetrics(lastResult)}

                            <div className={styles.cardFooter}>
                                {last?.error_message ? (
                                    <span
                                        className={styles.mutedText}
                                        title={last.error_message}
                                        style={{ color: '#b02a2a', fontStyle: 'normal' }}
                                    >
                                        Last run reported an error
                                    </span>
                                ) : (
                                    <span />
                                )}
                                <div className={styles.footerActions}>
                                {isRunning && (
                                    <button
                                        type="button"
                                        className={styles.resetButton}
                                        disabled={isResetting || refreshing}
                                        title="Clear a stuck 'running' state so a new scrape can be started (e.g. after a server restart interrupted the last run)."
                                        onClick={() => {
                                            setOpenMenuKey(null)
                                            handleReset(introducer)
                                        }}
                                    >
                                        {isResetting ? 'Resetting...' : 'Reset'}
                                    </button>
                                )}
                                <div
                                    className={styles.runButtonGroup}
                                    data-run-menu={introducer.key}
                                >
                                    <button
                                        type="button"
                                        className={styles.runButton}
                                        disabled={isRunning || isTriggering || refreshing}
                                        onClick={() => {
                                            setOpenMenuKey(null)
                                            handleTrigger(introducer)
                                        }}
                                    >
                                        {isRunning ? 'Running...' : isTriggering ? 'Starting...' : 'Run now'}
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.runButtonCaret}
                                        disabled={isRunning || isTriggering || refreshing}
                                        aria-haspopup="menu"
                                        aria-expanded={openMenuKey === introducer.key}
                                        aria-label="More run options"
                                        onClick={() =>
                                            setOpenMenuKey(
                                                openMenuKey === introducer.key
                                                    ? null
                                                    : introducer.key
                                            )
                                        }
                                    >
                                        ▾
                                    </button>
                                    {openMenuKey === introducer.key && (
                                        <div className={styles.runMenu} role="menu">
                                            <button
                                                type="button"
                                                className={styles.runMenuItem}
                                                role="menuitem"
                                                onClick={() => {
                                                    setOpenMenuKey(null)
                                                    handleTrigger(introducer, {
                                                        skipTransfer: false,
                                                    })
                                                }}
                                            >
                                                <strong>Run full scrape</strong>
                                                <span className={styles.runMenuItemHint}>
                                                    All 5 phases including Contact Fusion transfer
                                                </span>
                                            </button>
                                            <button
                                                type="button"
                                                className={styles.runMenuItem}
                                                role="menuitem"
                                                onClick={() => {
                                                    setOpenMenuKey(null)
                                                    handleTrigger(introducer, {
                                                        skipTransfer: true,
                                                    })
                                                }}
                                            >
                                                <strong>Run scrape (no transfer)</strong>
                                                <span className={styles.runMenuItemHint}>
                                                    Phases 1-4 only — skips CF transfer
                                                </span>
                                            </button>
                                        </div>
                                    )}
                                </div>
                                </div>
                            </div>
                        </div>
                    )
                })}
            </div>

            <h2 className={styles.sectionHeader}>Recent Runs (last {HISTORY_LIMIT})</h2>
            <div className={styles.tableWrapper}>
                <div className={styles.filterRow}>
                    <label htmlFor="introducer-filter">Filter by introducer:</label>
                    <select
                        id="introducer-filter"
                        value={filterIntroducer}
                        onChange={(e) => {
                            setFilterIntroducer(e.target.value)
                            setExpandedRunId(null)
                        }}
                    >
                        <option value="">All</option>
                        {introducers.map((i) => (
                            <option key={i.key} value={i.key}>
                                {i.label}
                            </option>
                        ))}
                    </select>
                </div>

                {filteredRuns.length === 0 ? (
                    <div className={styles.emptyText}>No runs recorded yet.</div>
                ) : (
                    <table className={styles.runsTable}>
                        <thead>
                            <tr>
                                <th>Introducer</th>
                                <th>Status</th>
                                <th>Trigger</th>
                                <th>Triggered By</th>
                                <th>Started</th>
                                <th>Finished</th>
                                <th>Duration</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredRuns.map((run) => {
                                const expanded = expandedRunId === run.id
                                const result = parseResultJson(run.result_json)
                                const errorsBundle = errorsByRunId[run.id]
                                const transferSkipped = wasTransferSkipped(run)
                                return (
                                    <Fragment key={run.id}>
                                        <tr
                                            className={expanded ? styles.expandedRow : ''}
                                            onClick={() => handleToggleExpand(run.id)}
                                        >
                                            <td>{run.introducer}</td>
                                            <td>
                                                <StatusBadge status={run.status} />
                                            </td>
                                            <td>
                                                <TriggerSourceBadge
                                                    triggeredBy={run.triggered_by}
                                                    transferSkipped={transferSkipped}
                                                />
                                            </td>
                                            <td>
                                                {run.triggered_by_user || (
                                                    <span className={styles.mutedText}>—</span>
                                                )}
                                            </td>
                                            <td>{formatDateTime(run.started_at)}</td>
                                            <td>
                                                {run.finished_at ? (
                                                    formatDateTime(run.finished_at)
                                                ) : (
                                                    <span className={styles.mutedText}>in progress</span>
                                                )}
                                            </td>
                                            <td>
                                                {run.total_operation_time || (
                                                    <span className={styles.mutedText}>—</span>
                                                )}
                                            </td>
                                        </tr>
                                        {expanded && (
                                            <tr className={styles.detailRow}>
                                                <td colSpan={7}>
                                                    <div className={styles.detailPanel}>
                                                        <div className={styles.detailMeta}>
                                                            <span>
                                                                <strong>Run ID:</strong> {run.id}
                                                            </span>
                                                            <span>
                                                                <strong>Created:</strong>{' '}
                                                                {formatDateTime(run.created_at)}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                className={styles.reloadErrorsButton}
                                                                onClick={(e) => {
                                                                    e.stopPropagation()
                                                                    loadRunErrors(run.id, true)
                                                                }}
                                                                disabled={errorsBundle?.loading}
                                                            >
                                                                {errorsBundle?.loading
                                                                    ? 'Reloading…'
                                                                    : 'Reload errors'}
                                                            </button>
                                                        </div>
                                                        <PhaseCountsStrip
                                                            phaseCounts={
                                                                errorsBundle?.phaseCounts || {}
                                                            }
                                                            loading={
                                                                !!errorsBundle?.loading &&
                                                                !errorsBundle?.loaded
                                                            }
                                                        />
                                                        {transferSkipped && (
                                                            <div className={styles.detailNoticeBox}>
                                                                <strong>Contact Fusion transfer skipped:</strong>{' '}
                                                                {result?.transferSkipReason ||
                                                                    'this run was triggered with skipTransfer=true. Phases 1-4 ran normally; Phase 5 was not invoked.'}
                                                            </div>
                                                        )}
                                                        {run.error_message && (
                                                            <div className={styles.detailErrorBox}>
                                                                <strong>
                                                                    Fatal error (run aborted):
                                                                </strong>{' '}
                                                                {run.error_message}
                                                            </div>
                                                        )}
                                                        <ScrapeRunErrorsPanel
                                                            bundle={errorsBundle}
                                                        />
                                                        {result ? (
                                                            <details
                                                                className={styles.resultJsonDetails}
                                                            >
                                                                <summary>
                                                                    Result payload (raw JSON)
                                                                </summary>
                                                                <pre className={styles.detailJsonBox}>
                                                                    {JSON.stringify(result, null, 2)}
                                                                </pre>
                                                            </details>
                                                        ) : (
                                                            <div className={styles.mutedText}>
                                                                No result payload recorded for this
                                                                run.
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </Fragment>
                                )
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    )
}
